import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "./Logo";
import { useLang } from "../i18n";

const LangSwitch = () => {
  const { lang, setLang, langs } = useLang();
  return (
    <div className="flex gap-1 text-[11px] tracking-[0.18em]" data-testid="lang-switcher">
      {langs.map((l) => (
        <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)}
          className={`px-1.5 py-1 uppercase transition-colors ${lang === l ? "text-charcoal border-b border-gold" : "text-muted hover:text-charcoal"}`}>
          {l === "zh" ? "中" : l}
        </button>
      ))}
    </div>
  );
};

export default function Navbar() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  useEffect(() => setOpen(false), [location]);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/atelier", label: t("nav.atelier") },
    { to: "/collezione", label: t("nav.collection") },
    { to: "/noleggio", label: t("nav.rental") },
    { to: "/appuntamenti", label: t("nav.appointments") },
  ];

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#FDFBF7]/85 backdrop-blur-md border-b border-softline" : "bg-transparent"}`}>
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-[76px] flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`nav-${l.to.slice(1) || "home"}`}
              className={({ isActive }) => `text-[11px] tracking-[0.22em] uppercase transition-colors ${isActive ? "text-gold" : "text-charcoal hover:text-gold"}`}>
              {l.label}
            </NavLink>
          ))}
          <LangSwitch />
          <Link to="/appuntamenti" data-testid="nav-book-cta" className="btn-solid !py-3 !px-6">{t("nav.book")}</Link>
        </nav>
        <div className="lg:hidden flex items-center gap-4">
          <LangSwitch />
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="text-charcoal" aria-label="Menu">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <><path d="M3 7h18" /><path d="M3 12h18" /><path d="M3 17h18" /></>}
            </svg>
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav className="lg:hidden bg-[#FDFBF7]/95 backdrop-blur-md border-t border-softline px-6 py-8 flex flex-col gap-5"
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-mobile-${l.to.slice(1) || "home"}`} className="font-serif text-2xl">{l.label}</Link>
            ))}
            <Link to="/appuntamenti" className="btn-solid mt-2">{t("nav.book")}</Link>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
